import { favoritesAssignWidgetReducer } from '@cian/favorites-assign-widget';
import { applyMiddleware, combineReducers, compose, createStore as _createStore } from 'redux';
import thunk, { ThunkDispatch } from 'redux-thunk';

import { authorizationTrapReducer } from '../../reducers/authorization_trap';
import { chatReducer } from '../../reducers/chat';
import { comparisonAlbumsReducer } from '../../reducers/comparisonAlbums';
import { complainReducer } from '../../reducers/complain';
import { configReducer } from '../../reducers/config/reducer';
import { countsReducer } from '../../reducers/counts';
import {
  dealTypeReducer,
  favoriteEntityTypeReducer,
  orderFieldReducer,
  showFiltersModalReducer,
} from '../../reducers/filters';
import { foldersReducer } from '../../reducers/folders';
import { lkTypeReducer } from '../../reducers/lkType';
import { mapReducer } from '../../reducers/map';
import { notificationsReducer } from '../../reducers/notifications';
import { offersByRealtyOfferIdReducer } from '../../reducers/offersByRealtyOfferId';
import { comparisonNotificationReducer } from '../../reducers/offersComparison';
import { printReducer } from '../../reducers/print';
import { removeAllConfirmReducer } from '../../reducers/removeAllConfirm';
import { resultsReducer } from '../../reducers/results';
import { selectedReducer } from '../../reducers/selected';
import { shareCollectionReducer } from '../../reducers/shareCollection';
import { IAbUseExperiment } from '../../types/abUseExperiment';
import { IApplicationContext } from '../../types/applicationContext';
import { IFiltersState } from '../../types/filters';
import { IPageState } from '../../types/page';
import { IApplicationState, TFilterActions, TReduxActions, IReduxStore } from '../../types/redux';
import { TUser } from '../../types/user';
import { savedSearchesReducer } from '../../reducers/savedSearches';
import { savedSearchesNotificationReducer } from '../../reducers/savedSearchesNotification';
import { activeTabReducer } from '../../reducers/activeTabReducer';
import { EFavoritePageType } from '../../types/favoritePageType';
import { recentlyViewedReducer } from '../../reducers/recentlyViewedReducer';
import { recommendationsReducer } from '../../reducers/recommendationsReducer';

interface IThunkExt {
  dispatch: ThunkDispatch<IApplicationState, IApplicationContext, TReduxActions>;
}

let storeReminder: IReduxStore;

/**
 * Хелпер для создания Redux стора
 * @param initialState IApplicationState - предзагруженное состояние
 */
export function createReduxStore(
  initialState: IApplicationState,
  context: IApplicationContext,
  debugEnabled: boolean,
): IReduxStore {
  const reducers = combineReducers<IApplicationState, TReduxActions>({
    config: configReducer,
    abUseExperiments: (state: IAbUseExperiment[] = initialState.abUseExperiments) => state,
    filters: combineReducers<IFiltersState, TFilterActions>({
      dealType: dealTypeReducer,
      favoriteEntityType: favoriteEntityTypeReducer,
      orderField: orderFieldReducer,
      showFiltersModal: showFiltersModalReducer,
    }),
    results: resultsReducer,
    folders: foldersReducer,
    notifications: notificationsReducer,
    page: (state: IPageState = initialState.page) => state,
    user: (state: TUser = initialState.user) => state,
    authorizationTrap: authorizationTrapReducer,
    print: printReducer,
    removeAllConfirm: removeAllConfirmReducer,
    counts: countsReducer,
    complain: complainReducer,
    selected: selectedReducer,
    favoritesAssignWidget: favoritesAssignWidgetReducer,
    shareCollection: shareCollectionReducer,
    comparisonNotification: comparisonNotificationReducer,
    map: mapReducer,
    offersByRealtyOfferId: offersByRealtyOfferIdReducer,
    lkType: lkTypeReducer,
    chat: chatReducer,
    locationId: (state: number = initialState.locationId) => state,
    savedSearches: savedSearchesReducer,
    savedSearchesNotification: savedSearchesNotificationReducer,
    activeTab: activeTabReducer,
    isEmailConfirmed: (state: boolean = initialState.isEmailConfirmed) => state,
    favoritePageType: (state: EFavoritePageType = initialState.favoritePageType) => state,
    recentlyViewed: recentlyViewedReducer,
    recommendations: recommendationsReducer,
    comparisonAlbums: comparisonAlbumsReducer,
  });

  const composeEnhancers = debugEnabled ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose : compose;

  const storeEnchancers = composeEnhancers(applyMiddleware(thunk.withExtraArgument(context)));

  const createStore = (): IReduxStore =>
    _createStore<IApplicationState, TReduxActions, IThunkExt, {}>(reducers, initialState, storeEnchancers);

  if (process.env.NODE_ENV === 'development' && debugEnabled) {
    if (!storeReminder) {
      storeReminder = createStore();
    }

    storeReminder.replaceReducer(reducers);

    return storeReminder;
  }

  return createStore();
}
